import { useEffect, useState } from "react";

const STYLE_ID = "google-fonts-global";

// Every family a component has asked for on this page. The style tag is kept in
// line with this set, never with component state: a font remembered as
// "loaded" while its @import was missing from the page stayed missing for good
// (TONY demo, 31 Oct 2026: the footer's Archivo fell back to serif).
const wantedFonts = new Set<string>();
let headObserver: MutationObserver | null = null;

const importRule = (font: string) =>
  `@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    font
  )}:wght@300;400;500;600;700&display=swap');`;

// Add an @import for each wanted family the style tag lacks; returns those added.
// Match the exact family in the import URL: a plain substring check skipped
// "Archivo" once "Archivo Black" was imported.
function syncGoogleFonts(): string[] {
  let globalStyle = document.getElementById(STYLE_ID) as HTMLStyleElement;
  if (!globalStyle) {
    globalStyle = document.createElement("style");
    globalStyle.id = STYLE_ID;
    document.head.appendChild(globalStyle);
  }

  const existingImports = globalStyle.textContent || "";
  const newFonts = Array.from(wantedFonts).filter(
    (font) => !existingImports.includes(`family=${encodeURIComponent(font)}:`)
  );
  if (newFonts.length === 0) return [];

  const newImports = newFonts.map(importRule).join("\n");
  globalStyle.textContent = existingImports
    ? existingImports + "\n" + newImports
    : newImports;
  return newFonts;
}

export function ensureGoogleFonts(fontFamilies: string[]): string[] {
  fontFamilies
    .map((font) => font?.trim())
    .filter(Boolean)
    .forEach((font) => wantedFonts.add(font));
  if (wantedFonts.size === 0) return [];

  // Put an import back if anything later rewrites or removes the style tag.
  // Our own write finds nothing missing on the next pass, so this settles.
  if (!headObserver && typeof MutationObserver !== "undefined") {
    headObserver = new MutationObserver(() => {
      if (wantedFonts.size > 0) syncGoogleFonts();
    });
    headObserver.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
  return syncGoogleFonts();
}

// Simplified version - loads all fonts as Google Fonts
// Custom fonts can be handled separately if needed
export function useGoogleFonts(fontFamilies: string[]) {
  const [fontsLoaded, setFontsLoaded] = useState<Set<string>>(new Set());
  const fontsKey = fontFamilies.join("|");

  useEffect(() => {
    try {
      const fonts = fontsKey.split("|").filter(Boolean);
      ensureGoogleFonts(fonts);

      // Give the browser a moment to start loading before reporting them
      const timer = setTimeout(() => {
        setFontsLoaded((prev) => {
          const next = new Set(prev);
          fonts.forEach((font) => next.add(font.trim()));
          return next;
        });
      }, 200);
      return () => clearTimeout(timer);
    } catch (error) {
      console.warn("Failed to load Google Fonts:", error);
    }
  }, [fontsKey]);

  return fontsLoaded;
}
